import { useState } from 'react';
import { api } from '../lib/api';
import { useAction, useApi } from '../lib/useApi';
import { Empty, ErrorBox, Field, Loading, Notice, PageHeader, LoadFailed , FormActions, useDraft } from '../components/ui';

interface Event {
  id: string;
  title: string;
  kind: string;
  happened_year: number | null;
  happened_month: number | null;
  happened_day: number | null;
  precision: 'exact' | 'month' | 'year' | 'decade' | 'unknown';
  place: string | null;
  note: string | null;
  person_id: string | null;
  person_name: string | null;
}
interface Person {
  id: string;
  display_name: string;
  relation_label: string | null;
}

const KIND_ICON: Record<string, string> = {
  birth: '👶🏾',
  naming: '🕯️',
  marriage: '💍',
  move: '🧳',
  work: '🛠️',
  education: '🎓',
  festival: '🥁',
  passing: '🕊️',
  other: '•',
};

const KINDS = ['birth', 'naming', 'marriage', 'move', 'work', 'education', 'festival', 'passing', 'other'];

/** "1954" reads as truth; "1 January 1954" invented to fill a form does not. */
function when(e: Event): string {
  if (!e.happened_year) return 'Date unknown';
  if (e.precision === 'decade') return `${Math.floor(e.happened_year / 10) * 10}s`;
  if (e.precision === 'year' || !e.happened_month) return String(e.happened_year);
  const month = new Date(2000, e.happened_month - 1, 1).toLocaleString(undefined, { month: 'long' });
  if (e.precision === 'month' || !e.happened_day) return `${month} ${e.happened_year}`;
  return `${e.happened_day} ${month} ${e.happened_year}`;
}

export function TimelinePage() {
  const { data, error, loading, reload } = useApi<{ events: Event[] }>('/timeline');
  const tree = useApi<{ people: Person[] }>('/tree');
  const act = useAction();
  const [form, setForm, clearDraft] = useDraft('timeline', { title: '', kind: 'other', year: '', month: '', personId: '', place: '', note: '', precision: 'year' });

  if (loading) return <Loading />;
  if (error || !data) return <LoadFailed error={error} onRetry={reload} />;
  const events = data.events;

  return (
    <>
      <PageHeader
        kicker="Family"
        title="Ancestral timeline"
        termKey="page.timeline"
        lead="The shape of your family across the years — births, moves, marriages, festivals, partings. Add only what you know; a remembered decade is worth more than an invented date."
      />
      <Notice text={act.notice} />
      <ErrorBox error={act.error} />

      <section className="card">
        {events.length === 0 ? (
          <Empty>Nothing here yet. Start with the earliest thing anyone remembers.</Empty>
        ) : (
          <ol className="timeline">
            {events.map((e) => (
              <li key={e.id} className="timeline-item">
                <span className="timeline-year">{when(e)}</span>
                <span className="timeline-dot" aria-hidden="true">
                  {KIND_ICON[e.kind] ?? '•'}
                </span>
                <div className="timeline-body">
                  <strong className="yo">{e.title}</strong>
                  <span className="muted small">
                    {e.person_name ? <span className="yo">{e.person_name}</span> : null}
                    {e.person_name && e.place ? ' · ' : null}
                    {e.place}
                  </span>
                  {e.note && <p className="small">{e.note}</p>}
                </div>
                <button
                  className="btn small ghost"
                  onClick={async () => {
                    if (!confirm(`Remove "${e.title}" from the timeline?`)) return;
                    await act.run(() => api(`/timeline/${e.id}`, { method: 'DELETE' }), 'Removed.');
                    reload();
                  }}
                >
                  Remove
                </button>
              </li>
            ))}
          </ol>
        )}
      </section>

      <form
        className="card stack"
        onSubmit={async (e) => {
          e.preventDefault();
          const ok = await act.run(
            () =>
              api('/timeline', {
                body: {
                  title: form.title,
                  kind: form.kind,
                  personId: form.personId || undefined,
                  year: form.year ? Number(form.year) : undefined,
                  month: form.month ? Number(form.month) : undefined,
                  precision: form.month ? 'month' : form.precision,
                  place: form.place || undefined,
                  note: form.note || undefined,
                },
              }),
            'Added to the timeline.',
          );
          if (ok) {
            setForm({ ...form, title: '', year: '', month: '', place: '', note: '' });
            reload();
          }
        }}
      >
        <h2>Add something that happened</h2>
        <Field id="tl-title" label="What happened" hint="In your own words — “Bàbá moved to Ìbàdàn”.">
          <input id="tl-title" className="yo" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required maxLength={160} />
        </Field>
        <div className="row">
          <select aria-label="Kind" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
            {KINDS.map((k) => (
              <option key={k} value={k}>
                {KIND_ICON[k]} {k}
              </option>
            ))}
          </select>
          <select aria-label="Who" value={form.personId} onChange={(e) => setForm({ ...form, personId: e.target.value })}>
            <option value="">Whole family</option>
            {(tree.data?.people ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.relation_label ? `${p.relation_label} ${p.display_name}` : p.display_name}
              </option>
            ))}
          </select>
        </div>
        <div className="row">
          <input aria-label="Year" placeholder="Year (e.g. 1954)" inputMode="numeric" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} />
          <input aria-label="Month" placeholder="Month (optional)" inputMode="numeric" value={form.month} onChange={(e) => setForm({ ...form, month: e.target.value })} />
          <select aria-label="How sure" value={form.precision} onChange={(e) => setForm({ ...form, precision: e.target.value })} disabled={!!form.month}>
            <option value="year">That year</option>
            <option value="decade">Around that decade</option>
            <option value="unknown">Not sure when</option>
          </select>
          <input aria-label="Place" placeholder="Place" className="yo" value={form.place} onChange={(e) => setForm({ ...form, place: e.target.value })} />
        </div>
        <Field id="tl-note" label="Anything else" hint="Optional.">
          <textarea id="tl-note" rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} maxLength={2000} />
        </Field>
        <FormActions submitLabel="Add to the timeline" busy={act.busy} onClear={clearDraft} />
      </form>
    </>
  );
}
